import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  DataGridPremium,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  type GridColDef,
  type GridPaginationModel,
  type GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import noDataAnim from "../../../assets/no-data-anim.webp";
import { withAssessmentCenterHeaderTooltips } from "../../AssessmentCenterPage/AssessmentCenterTruncatedColumnHeader";
import { getDataGridEmptyStateSx } from "../../shared/regulationsListDataGridSx";
import {
  formatAssessmentTemplateLabel,
  formatPlanImpactItemDisplay,
  type AutoTaggingPlan,
} from "../../../services/autoTaggingService";
import {
  AUTO_TAGGING_TABLE_HEADER_HEIGHT,
  AUTO_TAGGING_TABLE_ROW_HEIGHT,
  AUTO_TAGGING_TABLE_SX,
} from "./autoTaggingTableStyles";
import ReferenceItemsCell, {
  buildPlanReferenceItemDetails,
  formatReferenceItemsRnLabel,
} from "./ReferenceItemsCell";
import { TruncatedTooltipCell } from "./TruncatedTooltipCell";

/** Same empty asset/copy as Regulations List (homepage). */
function AutoTaggingPlansNoRowsOverlay() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        minHeight: 280,
        py: 4,
        boxSizing: "border-box",
        bgcolor: "background.paper",
      }}
    >
      <Box
        component="img"
        src={noDataAnim}
        alt=""
        sx={{ width: 160, height: "auto", mb: 2 }}
      />
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
        No search results found.
      </Typography>
    </Box>
  );
}

function EmptyDashCell() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        width: "100%",
        minWidth: 0,
      }}
    >
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>-</Typography>
    </Box>
  );
}

function OptionalTruncatedCell({ text }: Readonly<{ text: string }>) {
  const trimmed = text.trim();
  if (!trimmed) return <EmptyDashCell />;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        width: "100%",
        minWidth: 0,
      }}
    >
      <TruncatedTooltipCell text={trimmed} />
    </Box>
  );
}

/**
 * Fixed table height so the Start dialog stays the same size regardless of row count.
 * Pagination stays pinned at the bottom of this area (grid body scrolls when needed).
 */
export const AUTO_TAGGING_TABLE_HEIGHT = 420;

/** Same page-size options as Regulations List (homepage). */
export const AUTO_TAGGING_PLANS_PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;

/** Must match DataGrid layout width — CSS-only checkbox width desyncs header/body on horizontal scroll. */
const CHECKBOX_COLUMN_WIDTH = 80;

const COLUMN_WIDTHS = {
  id: 100,
  assessmentName: 220,
  impactItem: 240,
  referenceItems: 140,
  assessmentTemplate: 160,
} as const;

export type AutoTaggingPlansTableProps = Readonly<{
  rows: AutoTaggingPlan[];
  rowCount: number;
  loading?: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  selectionModel: GridRowSelectionModel;
  onSelectionModelChange: (model: GridRowSelectionModel) => void;
}>;

export default function AutoTaggingPlansTable({
  rows,
  rowCount,
  loading = false,
  paginationModel,
  onPaginationModelChange,
  selectionModel,
  onSelectionModelChange,
}: AutoTaggingPlansTableProps) {
  const columns = useMemo((): GridColDef<AutoTaggingPlan>[] => {
    const base: GridColDef<AutoTaggingPlan>[] = [
      {
        field: "id",
        headerName: "ID",
        width: COLUMN_WIDTHS.id,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ value }) => (
          <OptionalTruncatedCell text={String(value ?? "")} />
        ),
      },
      {
        field: "name",
        headerName: "Assessment Name",
        width: COLUMN_WIDTHS.assessmentName,
        flex: 0.6,
        minWidth: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) => row.name?.trim() || "",
        renderCell: ({ value }) => (
          <OptionalTruncatedCell text={String(value ?? "")} />
        ),
      },
      {
        field: "impactItem",
        headerName: "Impact item",
        flex: 1,
        minWidth: COLUMN_WIDTHS.impactItem,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) => formatPlanImpactItemDisplay(row),
        renderCell: ({ row }) => (
          <OptionalTruncatedCell text={formatPlanImpactItemDisplay(row)} />
        ),
      },
      {
        field: "referenceItems",
        headerName: "Reference Items",
        width: COLUMN_WIDTHS.referenceItems,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) =>
          formatReferenceItemsRnLabel(buildPlanReferenceItemDetails(row)),
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              minWidth: 0,
            }}
          >
            <ReferenceItemsCell plan={row} />
          </Box>
        ),
      },
      {
        field: "assessment_type",
        headerName: "Assessment Template",
        width: COLUMN_WIDTHS.assessmentTemplate,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) =>
          formatAssessmentTemplateLabel(row.assessment_type ?? ""),
        renderCell: ({ row }) => (
          <OptionalTruncatedCell
            text={formatAssessmentTemplateLabel(row.assessment_type ?? "")}
          />
        ),
      },
    ];
    return [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        field: "__check__",
        width: CHECKBOX_COLUMN_WIDTH,
        minWidth: CHECKBOX_COLUMN_WIDTH,
        maxWidth: CHECKBOX_COLUMN_WIDTH,
        resizable: false,
      },
      ...withAssessmentCenterHeaderTooltips(base),
    ];
  }, []);

  const isEmpty = rows.length === 0 && !loading;

  return (
    <Box sx={{ width: "100%", height: AUTO_TAGGING_TABLE_HEIGHT, flexShrink: 0 }}>
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        checkboxSelection
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={onSelectionModelChange}
        columnHeaderHeight={AUTO_TAGGING_TABLE_HEADER_HEIGHT}
        rowHeight={AUTO_TAGGING_TABLE_ROW_HEIGHT}
        loading={loading}
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[...AUTO_TAGGING_PLANS_PAGE_SIZE_OPTIONS]}
        disableColumnResize
        disableColumnReorder
        disableColumnVirtualization
        slots={{
          noRowsOverlay: AutoTaggingPlansNoRowsOverlay,
        }}
        sx={
          isEmpty
            ? [AUTO_TAGGING_TABLE_SX, getDataGridEmptyStateSx()]
            : AUTO_TAGGING_TABLE_SX
        }
      />
    </Box>
  );
}
